/**
 * Easter wrapping paper — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterWrappingPaperPack: ToilePack = {
    slug: 'easter-wrapping-paper',
    category: 'season',
    name: 'Easter wrapping paper',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'eggs', label: 'Tossed eggs', subject: 'Small painted eggs and daisies tossed evenly across the frame, no two touching'},
        {key: 'bunnies', label: 'Bunny rows', subject: 'Rows of small bunnies in profile alternating with tulips'},
        {key: 'carrots', label: 'Carrot stripe', subject: 'Carrots laid in diagonal rows with small leaves in the gaps'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel repeat', style: 'soft flat surface pattern, rounded motifs, even spacing, seamless repeat', palette: 'Pastel pink, mint, lilac and yellow on cream', framing: 'The motif repeated across the whole frame with even spacing, tileable edges'},
        {key: 'vintage', label: 'Vintage repeat', style: 'vintage easter paper, engraved motifs, slight grain, seamless repeat', palette: 'Rose, sage and cream', framing: 'The motif repeated across the whole frame, tileable edges'},
        {key: 'modern', label: 'Modern repeat', style: 'modern minimal surface design, flat simplified shapes, generous spacing, seamless repeat', palette: 'Two colours: sage green on white with a yellow accent', framing: 'Sparse repeat across the whole frame, tileable edges, no borders'},
    ],
    directions: ['a chick motif instead', 'in pastel blue and gold', 'a smaller repeat scale'],
    directionHint: 'try: a chick motif instead',
    seo: {
        title: 'Easter wrapping paper: eggs, bunnies, carrots',
        metaDescription: 'Nine Easter wrapping paper designs — tossed eggs, bunny rows, a carrot stripe — in pastel, vintage or modern repeat, tileable for the roll.',
        h1: 'Easter wrapping paper: tossed eggs, bunny rows, carrot stripe',
        tagline: 'Nine designs, three spring motifs, three seamless treatments each — ready to print at any width.',
        intro: 'Easter gift wrap as a seamless repeat: painted eggs and daisies tossed evenly, bunnies in profile alternating with tulips, carrots laid in diagonal rows. Each renders as a soft pastel repeat, a vintage engraved print, or a sparse modern minimal repeat, tileable edge to edge. Ask for a different motif, like a chick, and the same tileable structure carries it.',
        useCases: [
            'a printable gift wrap roll',
            'an Easter-basket packaging design',
            'a spring stationery flat lay',
            'a matching gift tag or card set',
        ],
        faq: [
            {q: 'Does the pattern tile without a visible seam?', a: 'Yes — every design is a seamless repeat with tileable edges, ready to print at any width for a roll.'},
            {q: 'Can I get a different motif, like a chick?', a: 'Yes — the direction field takes any motif, and the same tileable structure renders it in all three treatments.'},
            {q: "What's the difference between the three treatments?", a: 'Pastel is a soft rounded repeat, vintage has an engraved-print texture, and modern minimal is a sparse two-colour repeat.'},
            {q: 'What paper size does this print at?', a: 'The design is built to repeat, not a fixed sheet — print it as small or as wide as the roll needs.'},
        ],
    },
}
